import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, ShieldCheck, Droplets, Sun, Brush, Sparkles, Award } from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/a1668682-6cd0-4608-a6a8-d20136862eb0/images/d761d79564b7010609708efa40a8a610ada8698a9faf4f060260181bc1ee40da.png";
const COMPARISON_TEXTURE =
  "https://static.prod-images.emergentagent.com/jobs/a1668682-6cd0-4608-a6a8-d20136862eb0/images/e0f016fd68366f4fd81937972fc737b8c4f133722ecc6a3bf59b9ed4228aa7bb.png";
const PRODUCT_IMG =
  "https://images.unsplash.com/photo-1643822308521-1da534425d82?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const ARTIST_IMG =
  "https://images.unsplash.com/photo-1593382067395-ace3045a1547?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const FEATURES = [
  { icon: Droplets, title: "Yüksek Pigment", desc: "%40+ pigment yoğunluğu ile rakiplerden üstün renk derinliği." },
  { icon: Sun, title: "UV Dayanımı", desc: "Solmaya karşı 10 yıl garantili özel UV koruma katmanı." },
  { icon: Brush, title: "Pürüzsüz Akış", desc: "Fırça izlerini gizleyen ipek dokulu reolojik formülasyon." },
  { icon: ShieldCheck, title: "Toksik Değil", desc: "AP onaylı, çocuklar ve hassas yüzeyler için güvenli." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } })
      .then((r) => setFeatured(r.data.slice(0, 4)))
      .catch(() => {});
    api.get("/blog").then((r) => setPosts(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <>
      <SEO
        title="Premium Akrilik Hobi Boyaları"
        description="Wünberg Paint & Coating — yüksek pigment yoğunluklu, UV dayanımlı, profesyonel sanatçılar için akrilik hobi boyaları. Rakiplerden üstün kalite."
        image={HERO_BG}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Wünberg Paint & Coating",
          url: typeof window !== "undefined" ? window.location.origin : "",
          logo: "https://customer-assets.emergentagent.com/job_a1668682-6cd0-4608-a6a8-d20136862eb0/artifacts/fhi5ktps_IMG_9483.jpeg",
          sameAs: [],
        }}
      />

      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#030f26] via-[#030f26]/85 to-[#030f26]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#030f26] via-transparent to-transparent" />
        </div>

        <div className="wb-container relative z-10 py-32">
          <div className="max-w-3xl">
            <div className="wb-overline fade-up">Wünberg Paint & Coating</div>
            <h1 className="wb-h1 mt-5 fade-up fade-up-delay-1">
              Üstün Kalite,<br />
              <span className="text-[#d4af37] italic">Sınırsız</span> Yaratıcılık.
            </h1>
            <p className="wb-body mt-7 max-w-2xl fade-up fade-up-delay-2">
              Profesyonel sanatçılar ve tutkulu hobi severler için tasarlanmış,
              yüksek pigment yoğunluklu premium akrilik boyalar. Her vuruşta
              piyasanın çok ötesinde performans.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 fade-up fade-up-delay-3">
              <Link to="/urunler" data-testid="hero-cta-products" className="wb-btn-primary">
                Renk Paletini Keşfet <ArrowRight size={18} />
              </Link>
              <Link to="/kalite-karsilastirma" data-testid="hero-cta-comparison" className="wb-btn-secondary">
                Kalite Karşılaştırması
              </Link>
            </div>

            <div className="mt-14 grid grid-cols-3 max-w-xl gap-6 fade-up fade-up-delay-4">
              {[
                { v: "40+", l: "Pigment yoğunluğu" },
                { v: "10 Yıl", l: "Solmaz garanti" },
                { v: "120+", l: "Renk seçeneği" },
              ].map((s) => (
                <div key={s.l}>
                  <div className="font-serif text-3xl md:text-4xl text-[#d4af37]">{s.v}</div>
                  <div className="text-xs uppercase tracking-widest text-[#a0aabf] mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section data-testid="features-section" className="wb-section">
        <div className="wb-container">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div className="max-w-xl">
              <div className="wb-overline">Neden Wünberg?</div>
              <h2 className="wb-h2 mt-3">Piyasanın en iyisinin <span className="italic text-[#d4af37]">kanıtı</span>.</h2>
            </div>
            <p className="wb-body max-w-md">
              Wünberg, laboratuvarda formüle edilen her partide rakiplerin üzerinde
              ölçülebilir performans gösteren akrilik boyalar üretir.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6">
            <div
              className="md:col-span-8 lg:col-span-8 row-span-2 wb-card relative min-h-[420px] overflow-hidden"
              data-testid="feature-card-main"
              style={{
                backgroundImage: `url(${COMPARISON_TEXTURE})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-tr from-[#030f26] via-[#030f26]/60 to-transparent" />
              <div className="relative z-10 p-10 h-full flex flex-col justify-end">
                <Award className="text-[#d4af37] mb-4" size={32} />
                <h3 className="wb-h3 max-w-md">Pigment yoğunluğunda <span className="italic text-[#d4af37]">%40</span> üstünlük.</h3>
                <p className="wb-body mt-4 max-w-lg">
                  Independent lab testleri, Wünberg akrilik formülünün önde gelen
                  rakip markalara kıyasla %40'a kadar daha yüksek pigment yoğunluğu
                  sunduğunu doğruladı.
                </p>
              </div>
            </div>

            {FEATURES.map((f) => (
              <div
                key={f.title}
                className="md:col-span-4 lg:col-span-4 wb-card p-8"
                data-testid={`feature-card-${f.title}`}
              >
                <f.icon className="text-[#d4af37] mb-4" size={28} />
                <div className="font-serif text-xl mb-2">{f.title}</div>
                <p className="text-sm text-[#a0aabf] leading-relaxed">{f.desc}</p>
              </div>
            )).slice(0, 2)}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 mt-6">
            {FEATURES.slice(2).map((f) => (
              <div key={f.title} className="wb-card p-8" data-testid={`feature-card-${f.title}`}>
                <f.icon className="text-[#d4af37] mb-4" size={28} />
                <div className="font-serif text-xl mb-2">{f.title}</div>
                <p className="text-sm text-[#a0aabf] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT PREVIEW */}
      <section data-testid="products-preview" className="wb-section bg-[#020a1a]">
        <div className="wb-container">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <div className="wb-overline">Renk Paleti</div>
              <h2 className="wb-h2 mt-3">Öne Çıkan Ürünlerimiz</h2>
            </div>
            <Link to="/urunler" className="wb-btn-secondary" data-testid="products-preview-all">
              Tüm Renkleri Keşfet <ArrowRight size={16} />
            </Link>
          </div>

          {featured.length === 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <Link key={i} to="/urunler" className="wb-card group" data-testid={`featured-placeholder-${i}`}>
                  <div className="aspect-[4/5] overflow-hidden bg-[#0c1f44]">
                    <img src={PRODUCT_IMG} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-6">
                    <div className="wb-overline">Akrilik Seri</div>
                    <div className="font-serif text-lg mt-2">Wünberg Master {i}</div>
                    <div className="text-xs text-[#a0aabf] mt-1">500ml · Mat Finiş</div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featured.map((p) => (
                <Link key={p.id} to={`/urunler/${p.slug}`} className="wb-card group" data-testid={`featured-${p.slug}`}>
                  <div className="aspect-[4/5] overflow-hidden bg-[#0c1f44]">
                    <img
                      src={p.image_url || PRODUCT_IMG}
                      alt={p.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6">
                    <div className="wb-overline">{p.category}</div>
                    <div className="font-serif text-lg mt-2">{p.name}</div>
                    <div className="text-xs text-[#a0aabf] mt-1">{p.volume} · {p.short_description?.slice(0, 30)}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ABOUT STORY */}
      <section className="wb-section">
        <div className="wb-container grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <img src={ARTIST_IMG} alt="sanatçı" className="w-full h-[560px] object-cover" />
            <div className="absolute -bottom-6 -right-6 hidden md:flex w-44 h-44 bg-[#d4af37] text-[#030f26] flex-col items-center justify-center text-center">
              <Sparkles size={28} />
              <div className="font-serif text-lg mt-2 leading-tight">Sanatçı<br />Onaylı</div>
            </div>
          </div>
          <div>
            <div className="wb-overline">Hikayemiz</div>
            <h2 className="wb-h2 mt-3">El emeğine duyulan saygı, <span className="italic text-[#d4af37]">şişede</span>.</h2>
            <p className="wb-body mt-6">
              Wünberg, Avrupa'nın köklü boya geleneğinden ilham alır. Her renk,
              ressamların atölye masasında düşündüğü hassasiyetle laboratuvarımızda
              formüle edilir. Tek bir hedefimiz var: sanatçının vizyonu ile tuval
              arasındaki köprüyü mükemmelleştirmek.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "Üst düzey ham pigment seçimi",
                "Avrupa kalite standartları (EN 71-3)",
                "Bağımsız laboratuvar testleri ile sertifikalı",
                "Çevre dostu, su bazlı formülasyon",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm">
                  <span className="w-1.5 h-1.5 bg-[#d4af37] mt-2" /> {t}
                </li>
              ))}
            </ul>
            <Link to="/hakkimizda" className="wb-btn-secondary mt-10" data-testid="about-cta">
              Hikayeyi Oku <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* BLOG */}
      <section className="wb-section bg-[#020a1a]">
        <div className="wb-container">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <div className="wb-overline">Atölye Notları</div>
              <h2 className="wb-h2 mt-3">Blog &amp; Rehber</h2>
            </div>
            <Link to="/blog" className="wb-btn-secondary" data-testid="blog-preview-all">
              Tüm yazılar <ArrowRight size={16} />
            </Link>
          </div>

          {posts.length === 0 ? (
            <div className="p-10 wb-glass text-center text-[#a0aabf]">
              Blog yazıları yakında eklenecek.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {posts.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} className="wb-card" data-testid={`blog-card-${p.slug}`}>
                  <div className="aspect-[16/10] overflow-hidden bg-[#0c1f44]">
                    <img
                      src={p.cover_image || "https://images.unsplash.com/photo-1522410818928-5522dacd5066?w=900"}
                      alt={p.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6">
                    <div className="wb-overline">{(p.tags && p.tags[0]) || "Blog"}</div>
                    <div className="font-serif text-xl mt-2 leading-snug">{p.title}</div>
                    <div className="text-sm text-[#a0aabf] mt-2 line-clamp-2">{p.excerpt}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* DEALER CTA */}
      <section className="wb-section">
        <div className="wb-container">
          <div className="wb-glass p-10 md:p-16 relative overflow-hidden border-[#d4af37]/30">
            <div className="absolute -top-32 -right-32 w-96 h-96 bg-[#d4af37]/10 blur-3xl rounded-full" />
            <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
              <div className="max-w-xl">
                <div className="wb-overline">Bayilik Fırsatı</div>
                <h2 className="wb-h2 mt-3">Wünberg ailesine katılın.</h2>
                <p className="wb-body mt-4">
                  Premium akrilik kategorisinde Türkiye genelinde büyüyen bayi ağımıza
                  katılın — özel fiyatlandırma, eğitim ve pazarlama desteği ile.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link to="/bayi-basvuru" className="wb-btn-primary" data-testid="cta-dealer-apply">
                  Bayi Başvurusu
                </Link>
                <Link to="/iletisim" className="wb-btn-secondary" data-testid="cta-contact">
                  Bize Ulaşın
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
